/**
 * useAdminAlerts (Trello #192) — drives the admin notification centre.
 *
 * 1. Seeds recent history over HTTP (adminApi.listAdminAlerts), scoped server-side
 *    to the caller's admin stack.
 * 2. Opens an AppSync GraphQL subscription (onAdminAlert) for this build's stack so
 *    new platform events arrive live.
 * 3. Tracks unread via a per-stack localStorage "last seen" marker — the ticket asks
 *    for a small history + live alerts, not server-side per-admin read state.
 *
 * Everything degrades gracefully: if AppSync isn't configured the history still loads;
 * if the subscription errors the hook keeps the seeded history and reports disconnected.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import { adminApi, type AdminAlert, type AdminAlertType, type AdminAlertSeverity } from '../services/api';
import {
  ON_ADMIN_ALERT_SUBSCRIPTION,
  getAdminStack,
  type OnAdminAlertPayload,
} from '../graphql/adminAlerts';

const SUBSCRIPTION_INIT_ID = 'subscription-init';

function lastSeenKey(stack: string): string {
  return `adminAlerts:lastSeen:${stack}`;
}

function readLastSeen(stack: string): string {
  try {
    return localStorage.getItem(lastSeenKey(stack)) || '';
  } catch {
    return '';
  }
}

function writeLastSeen(stack: string, value: string): void {
  try {
    localStorage.setItem(lastSeenKey(stack), value);
  } catch {
    /* localStorage unavailable — unread just resets each session */
  }
}

/** Map the AppSync payload (metadata is an AWSJSON string) into an AdminAlert. */
function fromPayload(payload: OnAdminAlertPayload): AdminAlert {
  let metadata: Record<string, unknown> | undefined;
  if (payload.metadata) {
    try {
      metadata = JSON.parse(payload.metadata) as Record<string, unknown>;
    } catch {
      metadata = undefined;
    }
  }
  return {
    id: payload.id,
    stack: (payload.stack as AdminAlert['stack']) ?? 'intelligent-automation',
    type: payload.type as AdminAlertType,
    severity: (payload.severity as AdminAlertSeverity) ?? 'info',
    title: payload.title ?? '',
    message: payload.message ?? '',
    entityId: payload.entityId ?? undefined,
    entityType: (payload.entityType as AdminAlert['entityType']) ?? undefined,
    metadata,
    createdAt: payload.createdAt,
  };
}

export interface UseAdminAlertsResult {
  alerts: AdminAlert[];
  unreadCount: number;
  /** The most-recent alert that arrived live (for the transient toast); null after dismiss. */
  liveAlert: AdminAlert | null;
  connected: boolean;
  markAllRead: () => void;
  dismissLiveAlert: () => void;
}

export function useAdminAlerts(enabled = true): UseAdminAlertsResult {
  const stack = getAdminStack();
  const [alerts, setAlerts] = useState<AdminAlert[]>([]);
  const [lastSeen, setLastSeen] = useState<string>(() => readLastSeen(stack));
  const [liveAlert, setLiveAlert] = useState<AdminAlert | null>(null);
  const [connected, setConnected] = useState(false);
  // Merge an alert in, newest-first, de-duped by id.
  const upsert = useCallback((incoming: AdminAlert) => {
    setAlerts((prev) => {
      if (prev.some((a) => a.id === incoming.id)) return prev;
      return [incoming, ...prev].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    });
  }, []);

  // 1. Seed history. No "already seeded" ref guard: under React StrictMode the
  // effect mounts → cleans up (cancelling the first fetch) → remounts, so a ref
  // guard would skip the remount's fetch and leave history empty. Letting it
  // re-run is correct (prod mounts once; StrictMode dev fetches twice, harmless).
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const { alerts: history } = await adminApi.listAdminAlerts(50);
        if (!cancelled && Array.isArray(history)) {
          setAlerts(history.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
        }
      } catch {
        /* history fetch failed — live subscription may still populate the feed */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  // 2. Live subscription.
  useEffect(() => {
    if (!enabled || !import.meta.env.VITE_APPSYNC_API_URL) return;

    let sub: { unsubscribe: () => void } | undefined;
    try {
      const client = generateClient();
      const observable = client.graphql({
        query: ON_ADMIN_ALERT_SUBSCRIPTION,
        variables: { stack },
      }) as { subscribe: (handlers: { next: (v: unknown) => void; error: (e: unknown) => void }) => { unsubscribe: () => void } };

      sub = observable.subscribe({
        next: (event: unknown) => {
          setConnected(true);
          const payload = (event as { data?: { onAdminAlert?: OnAdminAlertPayload } })?.data?.onAdminAlert;
          if (!payload || payload.id === SUBSCRIPTION_INIT_ID) return;
          const alert = fromPayload(payload);
          upsert(alert);
          setLiveAlert(alert);
        },
        error: () => setConnected(false),
      });
    } catch {
      setConnected(false);
    }

    return () => {
      try {
        sub?.unsubscribe();
      } catch {
        /* noop */
      }
    };
  }, [enabled, stack, upsert]);

  const unreadCount = useMemo(
    () => alerts.filter((a) => a.createdAt > lastSeen).length,
    [alerts, lastSeen],
  );

  const markAllRead = useCallback(() => {
    const newest = alerts[0]?.createdAt ?? new Date().toISOString();
    writeLastSeen(stack, newest);
    setLastSeen(newest);
  }, [alerts, stack]);

  const dismissLiveAlert = useCallback(() => setLiveAlert(null), []);

  return { alerts, unreadCount, liveAlert, connected, markAllRead, dismissLiveAlert };
}
